import React, { useState, useEffect } from 'react';
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from 'react-router-dom';
import { getSensors } from '../../../services/sensors/sensorsService';
import { getUser, addUser, editUser, getRoles } from '../../../services/users/usersService';
import { getTimeZones } from '../../../services/timeZones/timeZonesService';
import { toCapitalizeCase } from '../../utils/ToCapitalizeCase';
import { BOOLEAN_OPTIONS, roleLabel } from '../../utils/Constants';
import UserContractsSection from '../../contracts/UserContractsSection';
import { CONTRACT_TYPES, formatDate, isOpen } from '../../contracts/contractLabels';
import { getUserContracts } from '../../../services/contracts/contractsService';

const booleanOptions = BOOLEAN_OPTIONS;

const UserView = () => {
    const { register, handleSubmit, reset, control, setValue, getValues, setError, clearErrors, formState: {errors} } = useForm({
        defaultValues: {
            password: '',
            sensors: [],
            timeZoneId: null,
            role: 'ROLE_USER'
        }
    });

    const [sensors, setSensors] = useState([]);
    // Las casillas de sensores leen getValues; esto fuerza a redibujarlas tras recargarlas
    const [, setSensorsVersion] = useState(0);
    const [timeZones, setTimeZones] = useState([]);
    const [roles, setRoles] = useState([]);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const navigate = useNavigate();
    const { userId } = useParams();

    const PHONE_PATTERN = /^\+?[1-9]\d{1,14}$/;
    // Misma regla que el backend (UserServiceV2Impl.verifyPassword)
    const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%^&+=*!?.])\S{8,}$/;

    useEffect(() => {
        // Cargar zonas horarias
        const fetchTimeZones = async () => {
            try {
                const data = await getTimeZones(1, 20);
                setTimeZones(data);
            } catch (error) {
                console.error('Error fetching time zones:', error);
            }
        };

        const fetchRoles = async () => {
            try {
                const data = await getRoles();
                setRoles(data);
            } catch (error) {
                console.error('Error fetching roles:', error);
            }
        };

        loadSensors();
        fetchTimeZones();
        fetchRoles();
    
        const fetchUser = async () => {
            if (userId !== 'newUser') {
                try {
                    const response = await getUser(userId);
                    reset({
                        ...response,
                        password: '',
                        active: response.active.toString(),
                        wantsNotifications: response.wantsNotifications.toString(),
                    });
                } catch (error) {
                    console.error(error);
                }
            }
        };

        fetchUser();
    }, [userId, reset]); 

    const handleSensorSelection = (sensorId) => {
        const currentSensors = getValues("sensors") || [];
        const updatedSensors = currentSensors.includes(sensorId)
          ? currentSensors.filter((id) => id !== sensorId)
          : [...currentSensors, sensorId];
    
        setValue("sensors", updatedSensors);
    };

    // Un cambio de contrato agrega o quita sensores del usuario: se recargan para que
    // "Guardar" no deshaga ese cambio con la lista vieja
    const reloadAssignedSensors = async () => {
        try {
            const response = await getUser(userId);
            setValue("sensors", response.sensors || []);
            setSensorsVersion((v) => v + 1);
        } catch (error) {
            console.error('Error al recargar los sensores del usuario: ', error);
        }
    };

    const loadSensors = async () => {
        try {
            const response = await getSensors(1, 1000);
            setSensors(response.content);
        } catch (error) {
            console.error('Error al cargar los sensores: ', error);
            alert("Error al cargar los sensores.");
        }
    }

    const togglePasswordVisibility = () => {
        setIsPasswordVisible((status) => !status);
    };

    const onSubmit = handleSubmit(async (data) => {
        // Admin y vendedor no necesitan sensores propios
        const needsSensors = !['ROLE_ADMIN', 'ROLE_SELLER'].includes(data.role);
        if (needsSensors && data.sensors.length === 0) {
            setError("sensors", {
                type: "manual",
                message: "Debes seleccionar al menos un sensor",
            });
            return;
        }

        clearErrors(["sensors"]);

        // Desmarcar un sensor termina su contrato abierto: se confirma si era alquiler, préstamo o tenía fecha de pago
        if (userId !== 'newUser') {
            let managed = [];
            try {
                managed = (await getUserContracts(userId)).filter((c) => isOpen(c)
                    && !data.sensors.includes(c.sensorId)
                    && (c.type === 'RENT' || c.type === 'TRIAL' || c.validUntil));
            } catch (error) {
                console.error('Error al revisar los contratos del usuario: ', error);
            }
            if (managed.length > 0) {
                const detail = managed.map((c) => `- ${c.sensorId}: ${CONTRACT_TYPES[c.type] || c.type}`
                    + (c.validUntil ? ` hasta ${formatDate(c.validUntil)}` : '')).join('\n');
                if (!window.confirm(`Al guardar se terminarán estos contratos y el usuario perderá el acceso:\n${detail}\n\n¿Continuar?`)) {
                    return;
                }
            }
        }

        const body = {
            user: {
                ...(userId !== 'newUser' && { email: userId }),
                ...Object.fromEntries(
                    Object.entries(data)
                        .filter(([key]) => key !== 'sensors' && key !== 'password')
                        .map(([key, value]) => [
                            key,
                            ['firstName', 'lastName', 'address'].includes(key)
                                ? toCapitalizeCase(value)
                                : key === 'email'
                                ? value.trim().toLowerCase()
                                : ['wantsNotifications', 'active'].includes(key) // 👈 aquí manejas ambos
                                ? value === true || value === "true"
                                : value
                        ])
                ),
                ...(data.password && { password: data.password })
            },
            sensors: data.sensors
        };            

        try {
            if (userId !== 'newUser') {
                const response = await editUser(userId, body);
                alert(response.message);
            }
            else {
                body.user.active = true;
                const response = await addUser(body);
                alert(response.message);
            }
            navigate('/users');
        } catch (error) {
            console.error('Error al enviar el formulario: ', error);
            // Muestra el motivo que devuelve el backend (ej. rol inválido) si viene
            alert(error.response?.data?.message || "Error al enviar el formulario. Por favor, intenta de nuevo.");
        }
    });

    const handleCancel = () => {
        navigate('/users');
    };

    return(
        <div className='flex flex-col p-3'>
            <div className="flex max-sm:flex-col items-center justify-between mb-4">
                <h1 className="text-3xl font-semibold text-blue-600">Usuario</h1>
            </div>

            <div className="w-full max-w-5xl mx-auto ">
                <form onSubmit={onSubmit} className="flex flex-col items-center space-y-4 pb-2">
                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="firstName" className="block font-semibold text-black mb-1"> Nombres <span className="text-red-500">*</span></label>
                            <input id="firstName" type="text" autoComplete="name"
                                name="firstName"
                                placeholder="Nombres"
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md"
                                {...register('firstName',{
                                    required: {
                                        value: true,
                                        message: 'Nombres son requeridos'
                                    },
                                    minLength:{
                                        value: 2,
                                        message: 'Nombres deben tener al menos 2 caracteres'
                                    },
                                    maxLength:{
                                        value: 30,
                                        message: 'Nombres deben tener máximo 30 caracteres'
                                    }
                                })}
                            />
                            {
                                errors.firstName && <span className="error">{errors.firstName.message}</span>
                            }
                        </div>

                        <div className='w-full'>
                            <label htmlFor="lastName" className="block font-semibold text-black mb-1"> Apellidos <span className="text-red-500">*</span></label>
                            <input id="lastName" type="text" autoComplete="family-name"
                                name="lastName"
                                placeholder="Apellidos"
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md"
                                {...register('lastName',{
                                    required: {
                                        value: true,
                                        message: 'Apellidos son requeridos'
                                    },
                                    minLength:{
                                        value: 2,
                                        message: 'Apellidos deben tener al menos 2 caracteres'
                                    },
                                    maxLength:{
                                        value: 30,
                                        message: 'Apellidos deben tener máximo 30 caracteres'
                                    }
                                })}
                            />
                            {
                                errors.lastName && <span className="error">{errors.lastName.message}</span>
                            }
                        </div>
                    </div>
                    
                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="email" className="block font-semibold text-black mb-1"> Email <span className="text-red-500">*</span></label>
                            <input id="email" type="email" autoComplete="off"
                                name="email"
                                placeholder="Email"
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md"
                                title="Email"
                                {...register("email",{
                                    required: {
                                        value: true,
                                        message: 'Email es requerido'
                                    },
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: "Ingrese un correo electrónico válido"
                                    }    
                                })}
                            />
                            {
                                errors.email && <span className="error">{errors.email.message}</span>
                            }
                        </div>
                    </div>

                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="password" className="block font-semibold text-black mb-1">
                                Contraseña {(userId === 'newUser') && <span className="text-red-500">*</span>}
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    type={isPasswordVisible ? "text" : "password"}
                                    autoComplete="off"
                                    name="password"
                                    placeholder="Contraseña"
                                    title="contrasenia"
                                    className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md"
                                    {...register("password", {
                                        required: {
                                            value: userId === 'newUser',
                                            message: 'Contraseña es requerida',
                                        },
                                        pattern: {
                                            value: PASSWORD_PATTERN,
                                            message: "La contraseña debe mínimo 8 caracteres, incluyendo al menos una letra mayúscula, una letra minúscula, un número y un carácter especial (@ # $ % ^ & + = * ! ? .), sin espacios.",
                                        },
                                    })}
                                />
                                <button
                                    type="button"
                                    onClick={togglePasswordVisibility}
                                    className="absolute flex items-center inset-y-0 right-0 px-3"
                                >
                                    <span className="material-icons text-blue-600">
                                        {isPasswordVisible ? 'visibility_off' : 'visibility'}
                                    </span>
                                </button>
                            </div>
                            {errors.password && <span className="error">{errors.password.message}</span>}
                        </div>

                        <div className='w-full'>
                            <label htmlFor="phone" className="block font-semibold text-black mb-1">
                                Teléfono <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="phone"
                                type="text"
                                autoComplete="off"
                                name="phone"
                                placeholder="Teléfono"
                                title="Teléfono"
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md"
                                {...register("phone", {
                                    required: {
                                        value: true,
                                        message: 'Teléfono es requerido',
                                    },
                                    pattern: {
                                        value: PHONE_PATTERN,
                                        message: "Teléfono debe contener solo números",
                                    },
                                })}
                            />
                            {errors.phone && <span className="error">{errors.phone.message}</span>}
                        </div>
                    </div>

                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="address" className="block font-semibold text-black mb-1"> Dirección <span className="text-red-500">*</span></label>
                            <input id="address" type="text" autoComplete="off"
                                name="address"
                                placeholder="Dirección"
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md"
                                {...register("address",{
                                    required: {
                                        value: true,
                                        message: 'Dirección es requerida'
                                    } 
                                })}
                            />
                            {
                                errors.address && <span className="error">{errors.address.message}</span>
                            }
                        </div>
                    </div>

                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="active" className="block font-semibold text-black mb-1"> Activo <span className="text-red-500">*</span></label>
                            <select id="active"
                                name="active" 
                                {...register("active",{
                                    required: {
                                        value: true,
                                        message: 'Activo es requerido'
                                    }    
                                })}
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                            >
                                <option value="" disabled> Seleccione </option>
                                {booleanOptions.map((option, index) => (
                                    <option key={index} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            {
                                errors.active && <span className="error">{errors.active.message}</span>
                            }
                        </div>

                        <div className='w-full'>
                            <label htmlFor="wantsNotifications" className="block font-semibold text-black mb-1"> Desea recibir notificaciones <span className="text-red-500">*</span></label>
                            <select id="wantsNotifications" 
                                name="wantsNotifications" 
                                {...register("wantsNotifications",{
                                    required: {
                                        value: true,
                                        message: 'Desea recibir notificaciones es requerido'
                                    }    
                                })}
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                            >
                                <option value="" disabled> Seleccione </option>
                                {booleanOptions.map((option, index) => (
                                    <option key={index} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            {
                                errors.wantsNotifications && <span className="error">{errors.wantsNotifications.message}</span>
                            }
                        </div>
                    </div>

                    {/* Selector de Zona Horaria */}
                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="timeZoneId" className="block font-semibold text-black mb-1">
                                Zona Horaria <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="timeZoneId"
                                {...register("timeZoneId", {
                                required: {
                                    value: true,
                                    message: 'Zona horaria es requerida'
                                }
                                })}
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                            >
                                <option value="" disabled>Selecciona una zona horaria</option>
                                {timeZones.map(tz => (
                                    <option key={tz.id} value={tz.id}>
                                        {tz.name} (UTC{tz.timeOffset})
                                    </option>
                                ))}
                            </select>
                            {
                                errors.timeZoneId && <span className="error">{errors.timeZoneId.message}</span>
                            }
                        </div>

                        <div className='w-full'>
                            <label htmlFor="role" className="block font-semibold text-black mb-1">
                                Rol <span className="text-red-500">*</span>
                            </label>
                            <Controller
                                name="role"
                                control={control}
                                rules={{ required: { value: true, message: 'Rol es requerido' } }}
                                render={({ field }) => {
                                    // Si el usuario tiene un rol oculto (no asignable), se muestra igual para no perderlo
                                    const options = field.value && !roles.includes(field.value)
                                        ? [...roles, field.value]
                                        : roles;
                                    return (
                                        <select
                                            id="role"
                                            {...field}
                                            value={field.value || ''}
                                            className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                                        >
                                            <option value="" disabled>Selecciona un rol</option>
                                            {options.map(role => (
                                                <option key={role} value={role}>
                                                    {roleLabel(role)}
                                                </option>
                                            ))}
                                        </select>
                                    );
                                }}
                            />
                            {
                                errors.role && <span className="error">{errors.role.message}</span>
                            }
                        </div>
                    </div>

                    <div className="w-full">
                        <h2 className="text-xl font-semibold text-blue-600 mb-2">Selección de Sensores</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 overflow-y-auto max-h-80 p-2">
                            {sensors.map((sensor) => (
                                <div
                                    key={sensor.id}
                                    className={`
                                        flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200 shadow-md
                                        ${getValues("sensors").includes(sensor.id) ? 'bg-blue-100 border-2 border-blue-600' : 'bg-gray-100 hover:bg-gray-200 border border-gray-300'}
                                    `}
                                    onClick={() => handleSensorSelection(sensor.id)}
                                >
                                    <Controller
                                        name="sensors"
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                type="checkbox"
                                                id={`sensor-checkbox-${sensor.id}`}
                                                value={sensor.id}
                                                checked={getValues("sensors").includes(sensor.id) || false}
                                                onChange={() => {}}
                                                className="mr-3 form-checkbox text-blue-600 focus:ring-blue-600 h-5 w-5 cursor-pointer"
                                            />
                                        )}
                                    />
                                    <span className="text-sm font-medium">{sensor.id}</span>
                                </div>
                            ))}
                        </div>
                        {errors.sensors && <p className="text-red-600">{errors.sensors.message}</p>}
                    </div>

                    <div className="flex flex-col md:flex-row items-center justify-center md:space-x-4">
                        <div className="w-full">
                            <button type="submit" className="flex flex-1 rounded w-full justify-center text-white bg-blue-600 hover:bg-blue-800 font-medium px-5 py-2.5 me-2 mb-2 transition duration-200">
                                Guardar
                            </button>
                        </div>

                        <div className="w-full">
                            <button type="button" onClick={handleCancel} className="flex flex-1 rounded w-full justify-center text-white bg-red-600 hover:bg-red-800 font-medium px-5 py-2.5 me-2 mb-2 transition duration-200">
                                Cancelar
                            </button>
                        </div>
                    </div>
                </form>

                {userId !== 'newUser' && (
                    <div className="mt-6 pb-4">
                        <UserContractsSection userEmail={userId} sensors={sensors} onChanged={reloadAssignedSensors} />
                    </div>
                )}
            </div>
        </div>
    );
}

export default UserView; 
